/**
 * Prototype UI — unified remittance + adjudication rows for the combined Remittances tab.
 */

import { eobTotals } from './mockEobs';
import { MOCK_DENIALS, type DenialRow, type DenialStatus } from './mockDenials';
import { MOCK_EOBS } from './mockEobs';
import { MOCK_REMITTANCES, type RemittanceMatchStatus, type RemittancePostingStatus } from './mockRemittances';

export type RemittanceOutcomeFilter = 'all' | 'paid' | 'denied' | 'unmatched';

export type RemittanceListOutcome = 'paid' | 'partial' | 'denied' | 'unmatched';

export interface RemittanceListRow {
  id: string;
  kind: 'claim' | 'era' | 'ack-rejection';
  date: string;
  clientName: string;
  payer: string;
  /** Value used by the outcome segment filter (`paid` includes partial payments). */
  filterOutcome: 'paid' | 'denied' | 'unmatched';
  /** Display outcome badge. */
  displayOutcome: RemittanceListOutcome;
  amountPaid: number;
  matchStatus?: RemittanceMatchStatus;
  postingStatus?: RemittancePostingStatus;
  carc?: string;
  rarc?: string;
  reason?: string;
  denialStatus?: DenialStatus;
  owner?: string;
  eraId?: string;
  eobId?: string;
  claimId?: string;
  paymentRef?: string;
}

function claimRowsFromEobs(): RemittanceListRow[] {
  return MOCK_EOBS.map((eob) => {
    const era = MOCK_REMITTANCES.find((r) => r.id === eob.eraId);
    const denial = eob.denialId ? MOCK_DENIALS.find((d) => d.id === eob.denialId) : undefined;
    const totals = eobTotals(eob);
    const filterOutcome: RemittanceListRow['filterOutcome'] =
      eob.outcome === 'denied' ? 'denied' : 'paid';

    return {
      id: `eob-row-${eob.id}`,
      kind: 'claim',
      date: eob.adjudicatedAt,
      clientName: eob.clientName,
      payer: eob.payer,
      filterOutcome,
      displayOutcome: eob.outcome === 'paid' ? 'paid' : eob.outcome,
      amountPaid: totals.paid,
      matchStatus: era?.matchStatus,
      postingStatus: era?.postingStatus,
      carc: eob.carcCodes[0],
      rarc: eob.rarcCodes[0],
      reason: denial?.denialReason,
      denialStatus: denial?.status,
      owner: denial?.owner,
      eraId: eob.eraId,
      eobId: eob.id,
      claimId: eob.claimId,
      paymentRef: era?.checkOrEftNumber,
    };
  });
}

function ackRejectionRows(): RemittanceListRow[] {
  const eobDenialIds = new Set(MOCK_EOBS.map((e) => e.denialId).filter(Boolean));

  return MOCK_DENIALS.filter((d) => !eobDenialIds.has(d.id)).map((denial) =>
    ackRejectionRowFromDenial(denial),
  );
}

function ackRejectionRowFromDenial(denial: DenialRow): RemittanceListRow {
  return {
    id: `denial-row-${denial.id}`,
    kind: 'ack-rejection',
    date: denial.dueDate,
    clientName: denial.clientName,
    payer: denial.payer,
    filterOutcome: 'denied',
    displayOutcome: 'denied',
    amountPaid: 0,
    carc: denial.carc,
    rarc: denial.rarc,
    reason: denial.denialReason,
    denialStatus: denial.status,
    owner: denial.owner,
    eobId: denial.eobId,
    claimId: denial.claimId,
  };
}

function unmatchedEraRows(): RemittanceListRow[] {
  return MOCK_REMITTANCES.filter(
    (era) => era.matchStatus === 'partial' || era.matchStatus === 'unmatched',
  ).map((era) => ({
    id: `era-row-${era.id}`,
    kind: 'era' as const,
    date: era.receivedAt,
    clientName: '—',
    payer: era.payer,
    filterOutcome: 'unmatched' as const,
    displayOutcome: 'unmatched' as const,
    amountPaid: era.totalPaid,
    matchStatus: era.matchStatus,
    postingStatus: era.postingStatus,
    eraId: era.id,
    eobId: era.eobIds[0],
    paymentRef: era.checkOrEftNumber,
    reason:
      era.matchStatus === 'partial'
        ? 'ERA partially matched — manual review required'
        : 'ERA unmatched — manual review required',
  }));
}

export function buildRemittanceListRows(): RemittanceListRow[] {
  return [...claimRowsFromEobs(), ...ackRejectionRows(), ...unmatchedEraRows()];
}

export function filterRemittanceListRows(
  rows: RemittanceListRow[],
  outcome: RemittanceOutcomeFilter,
): RemittanceListRow[] {
  if (outcome === 'all') return rows;
  if (outcome === 'paid') {
    return rows.filter((row) => row.filterOutcome === 'paid');
  }
  if (outcome === 'denied') {
    return rows.filter((row) => row.filterOutcome === 'denied');
  }
  return rows.filter((row) => row.filterOutcome === 'unmatched');
}
